import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';

import Loading from '@/components/loading/component';
import { useOpenCallUiConfig } from '@/open-call-types';

import { useResetStores } from '@/hooks/use-reset-stores';
import { RouteParams } from '@/routes';
import { useStages, useUser } from '@/state';
import * as permifyPermissions from '@repo/api/permify/constants';
import { NavigationBar, Tab } from '@repo/ui/components/custom';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { showSuccessToast } from '@repo/ui/components/ui/success-toast';
import { useCandidateSelection } from '@repo/ui/state';

export const DefaultLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openCallId, stageId } = useParams<RouteParams>();
  const { error, fetchStages, loading, stages, createStage, renameStage } =
    useStages();

  const { user, loading: loadingUser, error: userError } = useUser();
  const { viewMode, toggleViewMode } = useCandidateSelection();
  const [canCreateStage, setCanCreateStage] = useState(false);
  const { isUiConfigLoading, uiConfig: navigationUiConfig } =
    useOpenCallUiConfig();
  const dashboardPath =
    (location.state as { dashboardPath?: string } | null)?.dashboardPath ?? '/';

  //? useStage(dismissedStageId)?
  const dismissedStage = stages.find((s) => s.name === 'Dismissed');
  const juryStage = stages.find((s) => s.name === 'Jury Stage');
  const finalistsStage = stages.find((s) => s.name === 'Finalists');
  const isDismissedStage = stageId === dismissedStage?.id;
  const isJuryStage = stageId === juryStage?.id;
  const isFinalistsStage = stageId === finalistsStage?.id;

  const backButtonTitle = !isDismissedStage
    ? 'Vogue Open Casting 2025'
    : 'Back';

  const onEditTabNameHandler = async (tab: Tab) => {
    const newName = tab.name.trim();
    const existingStage = stages.find((s) => s.id === tab.id);
    if (existingStage && existingStage.name === newName) return;

    const updatedStage = await renameStage({
      openCallId: openCallId!,
      newName: tab.name,
      stageId: tab.id,
    });
    if (!updatedStage) return;
    showSuccessToast({
      description: 'Stage name saved',
    });
  };

  const tabs = useMemo(() => {
    const newStages = stages.filter(
      (s) => !Object.values(permifyPermissions.stageNames).includes(s.name),
    );
    setCanCreateStage(newStages.length < 3);
    return stages
      .filter((s) => s.id !== dismissedStage?.id)
      .map((stage) => ({
        name: stage.name,
        value: stage.count,
        canBeEdited: newStages.some((s) => s.id === stage.id),
        onTabClick: (tab: Tab) => {
          if (stageId === tab.id) {
            return;
          }
          navigate(`/open-calls/${openCallId}/stage/${tab.id}`);
        },
        onEditTabName: (tab: Tab) => {
          if (!newStages.find((s) => s.id === tab.id)) return;
          onEditTabNameHandler(tab);
        },
        id: stage.id,
      }));
  }, [stages, isDismissedStage, stageId]);

  useEffect(() => {
    if (openCallId) {
      fetchStages(openCallId);
    }
  }, [fetchStages, openCallId]);

  useEffect(() => {
    const shouldShowErrorMessage = error && !loading && !loadingUser;
    if (shouldShowErrorMessage) {
      showErrorToast({
        title: 'Error',
        description: error,
      });
    }
  }, [error, loading, loadingUser]);

  useResetStores();

  if (!user && !loadingUser && userError) {
    return <div>{userError.message}</div>;
  }
  if (loadingUser) {
    return <Loading />;
  }
  if (isUiConfigLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="flex flex-col w-full p-2 md:p-6 gap-4 h-screen bg-zinc-50/50">
        <NavigationBar
          openCallId={openCallId!}
          key={stageId}
          error={error}
          tabs={tabs}
          activeTab={stageId!}
          uiConfig={{
            bulkActions: navigationUiConfig.bulkActions,
            filterDefinitions: navigationUiConfig.filters.definitions,
            filtersEnabled: navigationUiConfig.filters.enabled,
          }}
          isDismissedStage={isDismissedStage}
          backButtonTitle={backButtonTitle}
          loading={Boolean(loading || (!stages?.length && !error))}
          dismissedCount={dismissedStage?.count ?? 0}
          canCreateStage={canCreateStage}
          onBackButtonClick={() =>
            isDismissedStage ? navigate(-1) : navigate(dashboardPath)
          }
          onDismissedFolderClick={() =>
            navigate(`/open-calls/${openCallId}/stage/${dismissedStage?.id}`)
          }
          onNewStageClick={async () => {
            if (!openCallId) {
              showErrorToast({
                description: 'No open call id found',
              });
              return;
            }
            const newStage = await createStage(openCallId!);
            if (!newStage) return;
            showSuccessToast({
              description: 'New stage created',
            });
          }}
          onSelectClick={toggleViewMode}
          onSearchClick={() => {}}
          viewMode={viewMode}
          isJuryStage={isJuryStage}
          isFinalistsStage={isFinalistsStage}
        />
        <Outlet />
      </div>
    </>
  );
};
